import React, { useEffect, useRef, useState } from 'react'
import { ReactiveVar } from '@apollo/client'
import { ClickAwayListener, Tooltip } from '@mui/material'
import { IHeaderParams } from 'ag-grid-enterprise'
import ascIcon from 'assets/icons/asc-icon.svg'
import descIcon from 'assets/icons/desc-icon.svg'
import filterDisabledIcon from 'assets/icons/filter-icon-disable.svg'
import filterActiveIcon from 'assets/icons/filter-icon.svg'
import sortIcon from 'assets/icons/sort-icon.svg'
import { isAssignmentsColumnHeaderRightClickMenuVar } from 'cache/adv-filtering'
import { ColumnHeaderMenuState } from '../../types'
import { useToolTipStyles } from './styles'

enum SORT_MODE {
  ASC = 1,
  DESC = 2,
  IN_ACTIVE = 3,
}

type SortModeType = {
  [key: number]: React.ReactNode
}

interface IAgCustomHeader extends IHeaderParams {
  tooltipFullName?: string
  tooltipDefinition?: string
  tooltipSource?: string
  iconTooltip?: string
  iconSrc?: string
  enableFilter?: boolean
  columnHeaderRightClickMenuVar?: ReactiveVar<ColumnHeaderMenuState | false>
}

const chooseSortType = (sortMode: number): 'asc' | 'desc' | null => {
  switch (sortMode) {
    case SORT_MODE.ASC:
      return 'asc'
    case SORT_MODE.DESC:
      return 'desc'
    case SORT_MODE.IN_ACTIVE:
      return null
    default:
      return null
  }
}

const reverseSortMap = (sortMode?: string | null): SORT_MODE => {
  switch (sortMode) {
    case 'asc':
      return SORT_MODE.ASC
    case 'desc':
      return SORT_MODE.DESC
    case null:
      return SORT_MODE.IN_ACTIVE
    default:
      return SORT_MODE.IN_ACTIVE
  }
}

const AgCustomHeader = ({
  displayName,
  enableMenu,
  enableSorting,
  showColumnMenu,
  setSort,
  api,
  column,
  tooltipFullName,
  tooltipDefinition,
  tooltipSource,
  iconTooltip,
  iconSrc,
  enableFilter,
  columnHeaderRightClickMenuVar,
}: IAgCustomHeader) => {
  // Initialize sortMode with current column sort state
  const [sortMode, setSortMode] = useState<SORT_MODE>(() => reverseSortMap(column.getSort()))
  const [menuActive, setMenuActive] = useState<boolean>(false)

  const isColumnFilterActive = enableFilter ? !!api.getFilterModel()?.[column.getColId()] : false

  const { classes } = useToolTipStyles()
  const refButton = useRef<HTMLDivElement | null>(null)
  const sortDef: SortModeType = {
    1: <img src={ascIcon} alt="Sort Ascending" />,
    2: <img src={descIcon} alt="Sort Descending" />,
    3: <img src={sortIcon} alt="No Sort" />,
  }

  const onMenuClicked = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    event.stopPropagation()
    event.preventDefault()
    setMenuActive(true)
    const sort = column.getSort()
    if (sort) {
      setSortMode(reverseSortMap(sort))
    } else {
      setSortMode(SORT_MODE.IN_ACTIVE)
    }
    // In AG Grid v32, use showColumnFilter to open only the filter popup
    if (api && enableFilter) {
      api.showColumnFilter(column.getColId())
    } else if (refButton.current) {
      showColumnMenu(refButton.current)
    }
  }

  // Subscribe to sort and menu visibility changes
  useEffect(() => {
    const handleSortChanged = () => {
      const sort = column.getSort()
      setSortMode(reverseSortMap(sort))
    }

    const handleMenuVisibleChanged = () => {
      if (!column.isMenuVisible()) {
        setMenuActive(false)
      }
    }

    column.addEventListener('sortChanged', handleSortChanged)
    column.addEventListener('menuVisibleChanged', handleMenuVisibleChanged)

    // Also listen to grid-wide sort changes if api is available
    if (api) {
      const onSortChanged = () => {
        const newSort = column.getSort()
        setSortMode(reverseSortMap(newSort))
      }

      api.addEventListener('sortChanged', onSortChanged)

      return () => {
        column.removeEventListener('sortChanged', handleSortChanged)
        column.removeEventListener('menuVisibleChanged', handleMenuVisibleChanged)
        api.removeEventListener('sortChanged', onSortChanged)
      }
    }

    return () => {
      column.removeEventListener('sortChanged', handleSortChanged)
      column.removeEventListener('menuVisibleChanged', handleMenuVisibleChanged)
    }
  }, [api, column])

  const onSortRequested = (
    newSortOrder: SORT_MODE,
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    setMenuActive(true)

    const sortOrder =
      newSortOrder + 1 > SORT_MODE.IN_ACTIVE ? SORT_MODE.ASC : ((newSortOrder + 1) as SORT_MODE)

    if ('type' in event && event.type === 'contextmenu') {
      return
    }

    setMenuActive(false)
    const sortType = chooseSortType(sortOrder)
    // Let AG Grid handle the sort, event listener will update the state
    setSort(sortType, event.shiftKey)
    setSortMode(sortOrder)
  }

  const sortButtons = (currentSortMode: SORT_MODE) => {
    return (
      <div
        onClick={(event) => onSortRequested(currentSortMode, event)}
        onTouchEnd={handleHeaderContextMenu}
        className="customHeaderMenuButton"
      >
        {sortDef[currentSortMode]}
      </div>
    )
  }

  const menuButtons = () => {
    if (!enableFilter) {
      return null
    }

    return (
      <div
        ref={refButton}
        className="customHeaderMenuButton"
        onClick={onMenuClicked}
        onTouchEnd={onMenuClicked}
      >
        <img
          src={isColumnFilterActive ? filterActiveIcon : filterDisabledIcon}
          alt={isColumnFilterActive ? 'Filter Active' : 'Filter Inactive'}
        />
      </div>
    )
  }

  const hasAnyTooltipContent = tooltipFullName ?? tooltipDefinition ?? tooltipSource

  const handleHeaderContextMenu = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    event.preventDefault()

    let mouseLikeEvent: ColumnHeaderMenuState['mouseEvent']

    if ('changedTouches' in event && event.changedTouches.length > 0) {
      const touch = event.changedTouches[0]
      mouseLikeEvent = {
        clientX: touch.clientX,
        clientY: touch.clientY,
      } as unknown as ColumnHeaderMenuState['mouseEvent']
    } else {
      mouseLikeEvent = event as unknown as ColumnHeaderMenuState['mouseEvent']
    }

    const payload: ColumnHeaderMenuState = {
      mouseEvent: mouseLikeEvent,
      currentColumn: column,
    }

    if (columnHeaderRightClickMenuVar) {
      columnHeaderRightClickMenuVar(payload)
    } else {
      isAssignmentsColumnHeaderRightClickMenuVar(payload)
    }
  }

  return (
    <div className="customHeaderItems" onContextMenu={handleHeaderContextMenu}>
      {hasAnyTooltipContent ? (
        <Tooltip
          title={
            <span>
              {tooltipFullName && <p className={classes.fullName}>{tooltipFullName}</p>}
              {tooltipDefinition && (
                <p className={classes.definition}>Metric definition: {tooltipDefinition}</p>
              )}
              {tooltipSource && <p className={classes.source}>Source: {tooltipSource}</p>}
            </span>
          }
          placement="top"
          arrow
          classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
        >
          <div className={classes.customHeaderLabel}>{displayName}</div>
        </Tooltip>
      ) : (
        <div
          className={classes.customHeaderLabel}
          onTouchEnd={(event) => {
            event.preventDefault()
            onSortRequested(sortMode, event)
          }}
        >
          {displayName}

          {iconSrc && (
            <Tooltip
              title={iconTooltip ?? ''}
              className={classes.columnHdrIcnTooltip}
              placement="top"
              arrow
            >
              <img src={iconSrc} alt={displayName} />
            </Tooltip>
          )}
        </div>
      )}
      <ClickAwayListener
        onClickAway={(event) => {
          const target = event.target as HTMLElement

          if (
            target?.closest?.(
              '.ag-menu, .ag-filter-menu, .ag-filter-body, .ag-set-filter, .ag-filter-popup'
            )
          ) {
            return
          }

          if (sortMode === SORT_MODE.IN_ACTIVE) {
            setMenuActive(false)
          }
        }}
      >
        <div
          onClick={() => !menuActive && setMenuActive(true)}
          className={`customHeaderMenuButtonList${
            menuActive || isColumnFilterActive || column.getSort() ? '-active' : ''
          }`}
        >
          {enableSorting && sortButtons(sortMode)}
          {enableMenu && menuButtons()}
        </div>
      </ClickAwayListener>
    </div>
  )
}

export default AgCustomHeader
